import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./selfreflection/emotionSelection.css";
import emotionData from "./selfreflection/emotionData.json";
import { db } from "./firebase";
import { collection, query, where, getDocs, doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";

export default function JournalSummariesTab({ user }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [journals, setJournals] = useState([]);
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJournalsForDate(selectedDate);
  }, [selectedDate, user?.email]);

  const fetchJournalsForDate = async (date) => {
    if (!user || !user.email) return;
    setLoading(true);
    setError(null);
    setSummary("");
    setJournals([]);
    
    // Format date to match how it's stored in Firestore (YYYY-MM-DD)
    const offset = date.getTimezoneOffset()
    const finalDate = new Date(date.getTime() - (offset*60*1000))
    const dateStr = finalDate.toISOString().split('T')[0];

    try {
      const q = query(
        collection(db, "journals"),
        where("email", "==", user.email),
        where("Date", "==", dateStr)
      );
      const querySnapshot = await getDocs(q);
      const entries = [];
      querySnapshot.forEach((doc) => {
        entries.push(doc.data());
      });
      setJournals(entries);

      // Try to fetch existing summary
      const summaryDocRef = doc(db, "journal_summaries", `${user.email}_${dateStr}`);
      const summarySnap = await getDoc(summaryDocRef);
      if (summarySnap.exists()) {
        setSummary(summarySnap.data().summary);
      } else if (entries.length > 0) {
        await generateSummaryForEntries(entries, dateStr);
      }
    } catch (err) {
      console.error("Error fetching journals or summary:", err);
      setError("Failed to fetch journal entries.");
    } finally {
      setLoading(false);
    }
  };

  const generateSummaryForEntries = async (entries, dateStr) => {
    if (!entries || entries.length === 0) return;

    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (!apiKey) {
      setError("Gemini API key is not configured. Please set REACT_APP_GEMINI_API_KEY in your .env file.");
      return;
    }

    let prompt = `Here are my journal entries for today:\n\n`;
    entries.forEach((entry, index) => {
      prompt += `Entry ${index + 1}:\n`;
      prompt += `- Emotion: ${entry.emotion}\n`;
      prompt += `- Behavior: ${entry.behaviour}\n`;
      prompt += `- Thoughts: ${entry.thoughts}\n`;
      prompt += `- Situation: ${entry.situations}\n`;
      prompt += `- Need: ${entry.need}\n`;
      prompt += `- Would like if: ${entry.makes}\n\n`;
    });
    prompt += `Please provide a short, empathetic summary of my day, focusing on my emotions, behaviors, and what made me feel that way. Address me as "You". Do not use markdown headers, just return a plain text or simple markdown summary.`;

    try {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-3.5-flash:generateContent?key=${apiKey}`, {
        method: "POST",
        headers: {
           "Content-Type": "application/json"
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: prompt
            }]
          }]
        })
      });

      if (!response.ok) {
        throw new Error(`API returned status: ${response.status}`);
      }

      const data = await response.json();
      const generatedText = data.candidates?.[0]?.content?.parts?.[0]?.text;
      
      if (generatedText) {
        setSummary(generatedText);

        try {
          const summaryDocRef = doc(db, "journal_summaries", `${user.email}_${dateStr}`);
          await setDoc(summaryDocRef, {
            email: user.email,
            date: dateStr,
            summary: generatedText,
            createdAt: serverTimestamp()
          });
        } catch (saveErr) {
          console.error("Error saving summary to Firestore:", saveErr);
        }
      } else {
        setError("Failed to parse response from Gemini API.");
      }
    } catch (err) {
      console.error("Gemini API Error:", err);
      setError(`Failed to generate summary: ${err.message}`);
    }
  };



  return (
    <div style={{ padding: "30px 20px", backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
     
      <h2 className="es-title" style={{ textAlign: "center", marginBottom: "30px" }}>Daily Journal Summary</h2>
      
      <div style={{ position: "relative", zIndex: 999, marginBottom: "25px", display: "flex", alignItems: "center", justifyContent: "center", gap: "15px" }}>
        <label style={{ fontWeight: "600", color: "#a46d5b" }}>Select Date:</label>
        <DatePicker
          selected={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          dateFormat="yyyy-MM-dd"
          maxDate={new Date()}
          className="user-profile-input"
          portalId="root-portal"
        />
      </div>

      {loading && !summary && <p>Loading...</p>}
      
      {error && <div style={{ padding: "12px", backgroundColor: "#FFEFEF", color: "#D62828", borderRadius: "8px", marginBottom: "20px" }}>{error}</div>}

      {!loading && journals.length === 0 && !error && (
        <p style={{ color: "#666", fontStyle: "italic", textAlign: "center" }}>No journal entries found for this date.</p>
      )}

      {summary && (
        <div className="journal-summary-container" style={{ backgroundImage: "url('/mountains.png')" }}>
          
          <div className="journal-summary-flex">
            
            {/* Avatar based on the primary emotion of the day */}
            <div className="journal-avatar">
              <img 
                src={`/emotions/${(journals.find(j => j.emotion)?.emotion || "happy").toLowerCase().replace(/\s+/g, '')}.png`} 
                alt="Avatar"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>

            {/* Cloud-like speech bubble */}
            <div className="journal-cloud-bubble">
              {summary}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
